import { useState, useRef, useEffect, useCallback, RefObject } from 'react';

interface SelectionBox {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  scrollTopAtStart: number; // Track scroll position when selection started
}

interface UseBoxSelectionOptions {
  containerRef: RefObject<HTMLDivElement | null>;
  itemRefs: RefObject<Map<string, HTMLElement>>;
  onSelect?: (ids: string[]) => void;
  disabled?: boolean;
  minBoxSize?: number; // Minimum size to trigger selection
}

interface UseBoxSelectionReturn {
  isSelecting: boolean;
  selectionBoxStyle: React.CSSProperties;
  handleMouseDown: (e: React.MouseEvent) => void;
}

export function useBoxSelection({
  containerRef,
  itemRefs,
  onSelect,
  disabled = false,
  minBoxSize = 5,
}: UseBoxSelectionOptions): UseBoxSelectionReturn {
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionBox, setSelectionBox] = useState<SelectionBox | null>(null);
  const isSelectingRef = useRef(false);

  // Check if two rectangles intersect
  const rectsIntersect = useCallback(
    (
      r1: { left: number; top: number; right: number; bottom: number },
      r2: { left: number; top: number; right: number; bottom: number }
    ) => {
      return !(
        r2.left > r1.right ||
        r2.right < r1.left ||
        r2.top > r1.bottom ||
        r2.bottom < r1.top
      );
    },
    []
  );

  // Handle mouse down for box selection
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      // Only start selection on left mouse button and not on interactive elements
      if (e.button !== 0) return;
      const target = e.target as HTMLElement;
      if (
        target.closest('button') ||
        target.closest('input') ||
        target.closest('[role="menu"]') ||
        target.closest('[data-no-select]')
      ) {
        return;
      }

      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const startX = e.clientX - rect.left;
      const startY = e.clientY - rect.top;
      const scrollTopAtStart = container.scrollTop;

      setIsSelecting(true);
      isSelectingRef.current = true;
      setSelectionBox({
        startX,
        startY,
        currentX: startX,
        currentY: startY,
        scrollTopAtStart,
      });
    },
    [containerRef, disabled]
  );

  // Handle mouse move and mouse up for box selection
  useEffect(() => {
    if (!isSelecting || !selectionBox) return;

    const handleMouseMove = (e: MouseEvent) => {
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const currentX = e.clientX - rect.left;
      const currentY = e.clientY - rect.top;

      setSelectionBox(prev => (prev ? { ...prev, currentX, currentY } : null));
    };

    const handleMouseUp = () => {
      const container = containerRef.current;
      if (!selectionBox || !container) {
        setIsSelecting(false);
        isSelectingRef.current = false;
        setSelectionBox(null);
        return;
      }

      // Calculate the actual positions in document coordinates (accounting for scroll)
      const currentScrollTop = container.scrollTop;
      const scrollDiff = currentScrollTop - selectionBox.scrollTopAtStart;

      // Convert viewport coordinates to content coordinates
      const startYInContent =
        selectionBox.startY + selectionBox.scrollTopAtStart;
      const currentYInContent = selectionBox.currentY + currentScrollTop;

      const boxLeft = Math.min(selectionBox.startX, selectionBox.currentX);
      const boxRight = Math.max(selectionBox.startX, selectionBox.currentX);
      const boxTop = Math.min(startYInContent, currentYInContent);
      const boxBottom = Math.max(startYInContent, currentYInContent);

      const boxWidth = boxRight - boxLeft;
      const boxHeight = Math.abs(
        selectionBox.currentY - selectionBox.startY + scrollDiff
      );

      // Only process if the selection box has meaningful size
      if (boxWidth > minBoxSize && boxHeight > minBoxSize) {
        const containerRect = container.getBoundingClientRect();
        const selectedIds: string[] = [];

        // Check each item for intersection
        itemRefs.current?.forEach((element, id) => {
          const itemRect = element.getBoundingClientRect();
          const itemTop = itemRect.top - containerRect.top + currentScrollTop;
          const itemBottom =
            itemRect.bottom - containerRect.top + currentScrollTop;
          const itemLeft = itemRect.left - containerRect.left;
          const itemRight = itemRect.right - containerRect.left;

          if (
            rectsIntersect(
              {
                left: boxLeft,
                top: boxTop,
                right: boxRight,
                bottom: boxBottom,
              },
              {
                left: itemLeft,
                top: itemTop,
                right: itemRight,
                bottom: itemBottom,
              }
            )
          ) {
            selectedIds.push(id);
          }
        });

        if (selectedIds.length > 0 && onSelect) {
          onSelect(selectedIds);
        }
      }

      setIsSelecting(false);
      isSelectingRef.current = false;
      setSelectionBox(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [
    isSelecting,
    selectionBox,
    containerRef,
    itemRefs,
    rectsIntersect,
    onSelect,
    minBoxSize,
  ]);

  // Calculate selection box style for rendering
  const selectionBoxStyle = useCallback((): React.CSSProperties => {
    if (!selectionBox) return { display: 'none' };
    const container = containerRef.current;
    if (!container) return { display: 'none' };

    const currentScrollTop = container.scrollTop;
    const scrollDiff = currentScrollTop - selectionBox.scrollTopAtStart;

    // Adjust startY for scroll difference
    const adjustedStartY = selectionBox.startY - scrollDiff;

    const left = Math.min(selectionBox.startX, selectionBox.currentX);
    const top = Math.min(adjustedStartY, selectionBox.currentY);
    const width = Math.abs(selectionBox.currentX - selectionBox.startX);
    const height = Math.abs(selectionBox.currentY - adjustedStartY);

    return {
      position: 'fixed',
      left: `${left + container.getBoundingClientRect().left}px`,
      top: `${top + container.getBoundingClientRect().top}px`,
      width: `${width}px`,
      height: `${height}px`,
      pointerEvents: 'none',
      zIndex: 9999,
    };
  }, [selectionBox, containerRef])();

  return {
    isSelecting,
    selectionBoxStyle,
    handleMouseDown,
  };
}

export default useBoxSelection;
